import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/PageHeader";
import { EligibilityPill } from "@/components/EligibilityPill";
import { MatchScore } from "@/components/MatchScore";
import { formatSalary, locationLabel } from "@/lib/format";
import { FreshnessBadge } from "@/components/FreshnessBadge";
import { RemoteScopeBadge } from "@/components/RemoteScope";
import { COUNTRIES, ARRANGEMENTS, EMPLOYMENT_TYPES, INDUSTRIES, COMMON_LANGUAGES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { MagnifyingGlass, FloppyDisk, Plus } from "@phosphor-icons/react";
import ParseJobDialog from "@/components/ParseJobDialog";

const ELIG_OPTIONS = [
  { v: "eligible", l: "Eligible" },
  { v: "possibly_eligible", l: "Possibly eligible" },
  { v: "not_eligible", l: "Not eligible" },
];
const ANY = "__any__";

const EMPTY = {
  keyword: "", country: "", work_from: "", arrangement: "", employment_type: "", industry: "",
  language: "", eligibility: [], min_match: 0, only_eligible: false, only_active: false, sort: "match",
};

export default function Jobs() {
  const [f, setF] = useState(EMPTY);
  const [jobs, setJobs] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saveOpen, setSaveOpen] = useState(false);
  const [searchName, setSearchName] = useState("");

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const load = useCallback(async () => {
    setLoading(true);
    const params = {
      min_match: f.min_match, only_eligible: f.only_eligible, only_active: f.only_active, sort: f.sort,
    };
    if (f.keyword) params.keyword = f.keyword;
    if (f.country) params.country = f.country;
    if (f.work_from) params.work_from = f.work_from;
    if (f.arrangement) params.arrangement = f.arrangement;
    if (f.employment_type) params.employment_type = f.employment_type;
    if (f.industry) params.industry = f.industry;
    if (f.language) params.language = f.language;
    if (f.eligibility.length) params.eligibility = f.eligibility.join(",");
    const { data } = await api.get("/jobs", { params });
    setJobs(data.jobs);
    setCount(data.count);
    setLoading(false);
  }, [f]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleElig = (v) => {
    set("eligibility", f.eligibility.includes(v) ? f.eligibility.filter((x) => x !== v) : [...f.eligibility, v]);
  };

  const saveSearch = async () => {
    try {
      await api.post("/saved-searches", { name: searchName || "Untitled search", criteria: f });
      toast.success("Search saved");
      setSaveOpen(false);
      setSearchName("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader
        title="Find jobs"
        subtitle="Eligibility is assessed against your profile before any match score is shown."
        action={
          <div className="flex gap-2">
            <ParseJobDialog onCreated={load} trigger={<Button variant="outline" data-testid="add-job-btn" className="border-slate-300 gap-2"><Plus weight="bold" /> Add a job</Button>} />
            <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="save-search-btn" className="border-slate-300 gap-2"><FloppyDisk weight="bold" /> Save search</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Save this search</DialogTitle></DialogHeader>
                <Label>Name</Label>
                <Input value={searchName} onChange={(e) => setSearchName(e.target.value)} placeholder="e.g. Remote research roles, no relocation" data-testid="save-search-name-input" />
                <DialogFooter><Button onClick={saveSearch} data-testid="save-search-confirm-btn" className="bg-slate-900 hover:bg-slate-800">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Filters */}
        <aside className="rounded-lg border border-slate-200 bg-white p-5 h-fit space-y-5" data-testid="job-filters">
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Keyword / role</Label>
            <div className="relative mt-1.5">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input value={f.keyword} onChange={(e) => set("keyword", e.target.value)} placeholder="Search…" className="pl-9" data-testid="job-search-input" />
            </div>
          </div>

          <div data-testid="work-from-filter">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Work from (my location)</Label>
            <Select value={f.work_from || ANY} onValueChange={(v) => set("work_from", v === ANY ? "" : v)}>
              <SelectTrigger className="mt-1.5 h-9" data-testid="filter-work-from"><SelectValue placeholder="Anywhere I live" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ANY}>Anywhere I live</SelectItem>
                {COUNTRIES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-slate-400 mt-1">Finds roles you could perform from here — regardless of the employer's country. Employer-country restrictions still apply honestly.</p>
          </div>
          <FilterSelect label="Employer country" value={f.country} onChange={(v) => set("country", v)} options={COUNTRIES} testid="filter-country" />
          <FilterSelect label="Arrangement" value={f.arrangement} onChange={(v) => set("arrangement", v)} options={ARRANGEMENTS} cap testid="filter-arrangement" />
          <FilterSelect label="Employment type" value={f.employment_type} onChange={(v) => set("employment_type", v)} options={EMPLOYMENT_TYPES} cap testid="filter-employment" />
          <FilterSelect label="Industry" value={f.industry} onChange={(v) => set("industry", v)} options={INDUSTRIES} testid="filter-industry" />
          <FilterSelect label="Language" value={f.language} onChange={(v) => set("language", v)} options={COMMON_LANGUAGES} testid="filter-language" />

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Eligibility status</Label>
            <div className="mt-2 space-y-2">
              {ELIG_OPTIONS.map((o) => (
                <label key={o.v} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox checked={f.eligibility.includes(o.v)} onCheckedChange={() => toggleElig(o.v)} data-testid={`filter-elig-${o.v}`} />
                  {o.l}
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Minimum match: {f.min_match}%</Label>
            <Slider value={[f.min_match]} onValueChange={(v) => set("min_match", v[0])} max={100} step={5} className="mt-3" data-testid="filter-min-match" />
          </div>

          <label className="flex items-center justify-between gap-2 pt-1 cursor-pointer">
            <span className="text-sm font-medium text-slate-800">Only jobs I can realistically apply for</span>
            <Switch checked={f.only_eligible} onCheckedChange={(v) => set("only_eligible", v)} data-testid="filter-only-eligible" />
          </label>

          <label className="flex items-center justify-between gap-2 cursor-pointer">
            <span className="text-sm font-medium text-slate-800">Only active jobs</span>
            <Switch checked={f.only_active} onCheckedChange={(v) => set("only_active", v)} data-testid="filter-only-active" />
          </label>

          <Button variant="ghost" onClick={() => setF(EMPTY)} data-testid="clear-filters-btn" className="w-full text-slate-500 hover:text-slate-900">Clear filters</Button>
        </aside>

        {/* Results */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-slate-600" data-testid="results-count">{loading ? "Searching…" : `${count} job${count === 1 ? "" : "s"}`}</p>
            <Select value={f.sort} onValueChange={(v) => set("sort", v)}>
              <SelectTrigger className="w-[180px] h-9" data-testid="sort-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="match">Best match</SelectItem>
                <SelectItem value="eligibility">Eligibility first</SelectItem>
                <SelectItem value="recent">Most recent</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="jobs-list">
            {!loading && jobs.length === 0 && <p className="p-8 text-sm text-slate-500 text-center">No jobs match your filters.</p>}
            {jobs.map((j) => (
              <Link key={j.id} to={`/jobs/${j.id}`} data-testid={`job-row-${j.id}`}
                className="block px-5 py-4 border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors">
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-900">{j.title}</p>
                    <p className="text-sm text-slate-600 mt-0.5">{j.employer} · {locationLabel(j)}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <RemoteScopeBadge summary={j.remote_summary} />
                      {j.employment_type && <span>· {j.employment_type}</span>}
                      {formatSalary(j.salary_min, j.salary_max, j.salary_currency) && <span className="font-mono-num">· {formatSalary(j.salary_min, j.salary_max, j.salary_currency)}</span>}
                      {j.blocker_count > 0 && <span className="text-red-600">· {j.blocker_count} blocker{j.blocker_count > 1 ? "s" : ""}</span>}
                      <FreshnessBadge freshness={j.freshness} />
                      {j.source && <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[11px] font-medium text-slate-500" data-testid="source-badge">{j.source}</span>}
                      {j.duplicate_count > 1 && <span className="inline-flex items-center rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px] text-slate-400" data-testid="dup-sources" title={(j.also_via || []).join(", ")}>· also via {j.duplicate_count - 1} more source{j.duplicate_count - 1 > 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <EligibilityPill status={j.eligibility_status} />
                    <MatchScore score={j.match_score} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, cap, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
      <Select value={value || ANY} onValueChange={(v) => onChange(v === ANY ? "" : v)}>
        <SelectTrigger className="mt-1.5 h-9" data-testid={testid}><SelectValue placeholder="Any" /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ANY}>Any</SelectItem>
          {options.map((o) => (
            <SelectItem key={o} value={o}>{cap ? o.charAt(0).toUpperCase() + o.slice(1) : o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
